export interface DriveNote {
  version: 1;
  title: string;
  tags: string[];
  html: string;
  createdAt: string;
  updatedAt: string;
}

export interface DriveFile {
  id: string;
  name: string;
  modifiedTime: string;
}

const DRIVE_API = "https://www.googleapis.com/drive/v3/files";
const UPLOAD_API = "https://www.googleapis.com/upload/drive/v3/files";

function authHeaders(token: string) {
  return { Authorization: `Bearer ${token}` };
}

export async function getGoogleProfile(token: string) {
  const response = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
    headers: authHeaders(token),
  });
  if (!response.ok) throw new Error("Google profile could not be loaded.");
  return response.json() as Promise<{ name: string; email: string; picture: string }>;
}

export async function listTechoNotes(token: string): Promise<DriveFile[]> {
  const query = encodeURIComponent("appProperties has { key='techo' and value='note' } and trashed=false");
  const fields = encodeURIComponent("files(id,name,modifiedTime)");
  const response = await fetch(`${DRIVE_API}?q=${query}&spaces=drive&orderBy=modifiedTime%20desc&fields=${fields}`, {
    headers: authHeaders(token),
  });
  if (!response.ok) throw new Error("Notes could not be loaded from Google Drive.");
  const result = await response.json() as { files?: DriveFile[] };
  return result.files ?? [];
}

export async function readTechoNote(token: string, fileId: string): Promise<DriveNote> {
  const response = await fetch(`${DRIVE_API}/${fileId}?alt=media`, { headers: authHeaders(token) });
  if (!response.ok) throw new Error("This note could not be opened.");
  return response.json() as Promise<DriveNote>;
}

function multipartBody(note: DriveNote, name: string) {
  const boundary = `techo_${crypto.randomUUID()}`;
  const metadata = {
    name: `${name.replace(/[\\/:*?"<>|]/g, "").trim() || "Untitled"}.techo`,
    mimeType: "application/json",
    appProperties: { techo: "note" },
  };
  const body = [
    `--${boundary}`,
    "Content-Type: application/json; charset=UTF-8",
    "",
    JSON.stringify(metadata),
    `--${boundary}`,
    "Content-Type: application/json",
    "",
    JSON.stringify(note),
    `--${boundary}--`,
  ].join("\r\n");
  return { boundary, body };
}

export async function saveTechoNote(token: string, note: DriveNote, fileId?: string) {
  const { boundary, body } = multipartBody(note, note.title);
  const endpoint = fileId ? `${UPLOAD_API}/${fileId}?uploadType=multipart` : `${UPLOAD_API}?uploadType=multipart`;
  const response = await fetch(endpoint, {
    method: fileId ? "PATCH" : "POST",
    headers: { ...authHeaders(token), "Content-Type": `multipart/related; boundary=${boundary}` },
    body,
  });
  if (!response.ok) throw new Error("Google Drive could not save this note.");
  return response.json() as Promise<{ id: string; name: string }>;
}

export async function deleteTechoNote(token: string, fileId: string) {
  const response = await fetch(`${DRIVE_API}/${fileId}`, { method: "DELETE", headers: authHeaders(token) });
  if (!response.ok) throw new Error("This note could not be moved to the bin.");
}
